import { useEffect, useState } from "react";
import { AppImage } from "@/shared/ui/AppImage";
import {
  defaultDisplayAppearance,
  type PublicDisplay,
} from "../../domain/contracts/live-workspace";

const moneyFormatter = new Intl.NumberFormat("lo-LA");
const money = (value: number) => moneyFormatter.format(value);
/** หน้าจอและ preview ใช้ renderer เดียวกัน; แบ่ง variant ทุกหน้าโดยไม่ตัดทิ้ง */
export function LiveOverlay({ display }: { display: PublicDisplay }) {
  const [page, setPage] = useState(0);
  const portrait = display.settings.orientation === "portrait";
  const a = display.settings.appearance ?? defaultDisplayAppearance;
  const pageSize = a.variantsPerPage;
  const pages = Math.max(
    1,
    Math.ceil((display.product?.variants.length ?? 0) / pageSize),
  );
  const variantsMinHeight = `calc(${pageSize * 1.6}em + ${pageSize}cqw)`;
  useEffect(() => {
    setPage(0);
  }, [display.product?.id]);
  useEffect(() => {
    if (pages < 2) return;
    const timer = setInterval(
      () => setPage((p) => (p + 1) % pages),
      a.pageSeconds * 1000,
    );
    return () => clearInterval(timer);
  }, [pages, a.pageSeconds]);
  const product = display.product;
  const visibleVariants = product?.variants.slice(
    (page % pages) * pageSize,
    ((page % pages) + 1) * pageSize,
  );
  const emptyVariantSlots = ["1", "2", "3", "4", "5"].slice(
    0,
    Math.max(0, pageSize - (visibleVariants?.length ?? 0)),
  );
  return (
    <div
      data-testid="live-overlay"
      style={{
        containerType: "inline-size",
        background:
          display.settings.background === "solid"
            ? a.canvasColor
            : "transparent",
        color: a.textColor,
        fontFamily:
          a.fontFamily === "sans"
            ? 'Arial, "Noto Sans Lao", sans-serif'
            : a.fontFamily === "serif"
              ? 'Georgia, "Noto Serif Lao", serif'
              : "inherit",
        width: "100%",
      }}
    >
      {product && (
        <div style={{ width: "100%", containerType: "inline-size" }}>
          <div
            style={{
              borderRadius: `${a.radius}cqw`,
              background: `${a.cardColor}${Math.round(a.opacity * 2.55)
                .toString(16)
                .padStart(2, "0")}`,
              border: `${a.borderWidth}px solid ${a.borderColor}`,
              boxShadow: a.shadow ? "0 1cqw 3cqw #00000055" : "none",
              padding: `${a.padding}%`,
              display: "grid",
              gridTemplateColumns: portrait
                ? "1fr"
                : "repeat(auto-fit, minmax(min(100%, max(16rem, 45cqw)), 1fr))",
              gap: "2cqw",
              fontSize: `max(12px, ${((portrait ? 3.7 : 1.6) * a.fontScale) / 100}cqw)`,
              lineHeight: 1.6,
            }}
          >
            <div style={{ display: "flex", gap: "2cqw", alignItems: "center" }}>
              {product.imageUrl && (
                <div
                  style={{
                    width: `${((portrait ? 26 : 13) * a.imageScale) / 100}cqw`,
                    height: `${((portrait ? 26 : 13) * a.imageScale) / 100}cqw`,
                    flexShrink: 0,
                    borderRadius: "1cqw",
                    background: "white",
                    overflow: "hidden",
                  }}
                >
                  <AppImage
                    src={product.imageUrl}
                    alt=""
                    loading="eager"
                    fit={a.imageFit}
                    className="size-full"
                  />
                </div>
              )}
              {product.title && (
                <strong
                  style={{
                    fontSize: `max(16px, ${((portrait ? 4.4 : 2.2) * a.fontScale) / 100}cqw)`,
                    overflowWrap: "anywhere",
                  }}
                >
                  {product.title}
                </strong>
              )}
            </div>
            <div style={{ minHeight: variantsMinHeight }}>
              {visibleVariants?.map((v) => (
                <div
                  key={v.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1cqw",
                    borderBottom: `1px solid ${a.borderColor}`,
                    padding: ".5cqw 0",
                    height: "1.6em",
                    boxSizing: "content-box",
                    overflow: "hidden",
                  }}
                >
                  <div style={{ minWidth: 0, overflow: "hidden" }}>
                    {v.cfCode && (
                      <strong
                        style={{
                          color: a.codeColor,
                          marginRight: "1cqw",
                          fontSize: "1.2em",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {v.cfCode}
                      </strong>
                    )}
                    {v.remaining !== undefined && (
                      <small
                        style={{ color: a.mutedColor, whiteSpace: "nowrap" }}
                      >
                        {v.remaining === null
                          ? "Preorder"
                          : `ຄົງເຫຼືອ ${v.remaining}`}
                      </small>
                    )}
                  </div>
                  {v.salePrice !== undefined && (
                    <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {v.price !== undefined && v.price > v.salePrice && (
                        <s
                          style={{
                            color: a.mutedColor,
                            fontSize: ".8em",
                            marginRight: "1cqw",
                          }}
                        >
                          {money(v.price)}
                        </s>
                      )}
                      <strong style={{ color: a.priceColor }}>
                        {money(v.salePrice)} ກີບ
                      </strong>
                    </div>
                  )}
                </div>
              ))}
              {emptyVariantSlots.map((slot) => (
                <div
                  aria-hidden
                  key={`empty-variant-slot-${slot}`}
                  style={{
                    borderBottom: "1px solid transparent",
                    padding: ".5cqw 0",
                    height: "1.6em",
                    boxSizing: "content-box",
                  }}
                />
              ))}
              {pages > 1 && (
                <small>
                  {(page % pages) + 1} / {pages}
                </small>
              )}
            </div>
            {display.orders.length > 0 && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  gap: "1.4cqw",
                  flexWrap: "wrap",
                  borderTop: `1px solid ${a.borderColor}`,
                  paddingTop: "1cqw",
                  fontSize: ".8em",
                }}
              >
                {display.orders.map((order) => (
                  <span key={order.reference}>
                    {order.orderCode ?? order.reference} · {order.cfCode} ×{" "}
                    {order.quantity}
                    {order.name ? ` · ${order.name}` : ""}
                  </span>
                ))}
              </div>
            )}
            {display.next.length > 0 && (
              <div style={{ gridColumn: "1 / -1", fontSize: ".8em" }}>
                ຖັດໄປ: {display.next.map((p) => p.title).join(" → ")}
              </div>
            )}
            {display.totals && (
              <div style={{ gridColumn: "1 / -1" }}>
                ຮັບ CF {display.totals.claimQuantity} ຊິ້ນ ·{" "}
                {money(display.totals.claimValue)} ກີບ
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
